import { ref, computed } from 'vue';
import { decode } from 'he';
import { toArray } from 'lodash-es';
import { defineStore } from 'pinia';
import { findAll } from '@ui/helpers/string';

const htmlToText = (html) => decode(html
  .replace(/<\/p>/g, ' ')
  .replace(/<\/div>/g, ' ')
  .replace(/<\/h\d>/g, ' ')
  .replace(/<\/ul>/g, ' ')
  .replace(/<\/ol>/g, ' ')
  .replace(/<\/li><li[^>]*>/g, ' ')
  .replace(/<[^>]*>/g, ''));

// I'd like to have this in testing/staging for a while.
// It should help catch the cases where highlight might be misplaced
// TODO: remove hostname check
const isCustomerFacingEnvironment = [
  'app.datapeople.io',
  'sandbox.datapeople.io',
  'test-app.taprecruit.co',
].includes(window?.location?.hostname);

// finds the field highlight belongs to, its occurrence in the field text,
// and how much the highlight shifted
const findFieldOccurrence = (
  fields,
  {
    offset, length, highlightedText, type,
  },
  isRescore,
) => {
  const highlightedTextNoNewLines = highlightedText?.replace(/\n/g, ' ');

  // find a field for which the last character (offset+length) of the highlight
  // is within start to start+textLength range
  const field = fields.find(
    ({ start, textLength }) => start <= offset + length && offset + length <= start + textLength,
  );

  if (!field || !highlightedText) {
    if (!isCustomerFacingEnvironment && isRescore && type !== 'title') {
      throw new Error(
        `Cannot find a field for highlight: ${offset} ${length} ${highlightedTextNoNewLines}`,
      );
    }
    return { fieldId: '', occurrence: -1, shift: 0 };
  }

  // highlight offset is relative to the whole job description
  // expectedOffset is relative to its field
  const expectedOffset = offset - field.start;

  // highlightedText may appear multiple times in the field text
  const allIndices = findAll(field.text, highlightedTextNoNewLines);

  if (allIndices.length === 0 && highlightedTextNoNewLines
    && !isCustomerFacingEnvironment && isRescore
  ) {
    // TODO: investigate https://testing.iapp.datapeople.io/app/view/14903694
    const error = new Error('Highlight text not found in the field content at all');
    error.highlightedText = highlightedTextNoNewLines;
    error.fieldText = field.text;
    throw error;
  }

  // the highlight might be shifted due to compound unicode symbols (like emoji).
  // find the closest occurrence
  const occurrence = allIndices.reduce((res, _, idx) => {
    const resDiff = Math.abs(allIndices[res] - expectedOffset);
    const idxDiff = Math.abs(allIndices[idx] - expectedOffset);
    return idxDiff < resDiff ? idx : res;
  }, 0);

  // how much the highlight shifted
  const shift = allIndices[occurrence] - expectedOffset;

  return { fieldId: field.id, occurrence, shift };
};

// add fieldId and occurrence to each highlight
const adjustHighlights = (fields, highlights, isRescore) => {
  let cumulativeShift = 0;
  return highlights.map((highlight) => {
    const adjustedOffset = highlight.offset + cumulativeShift;
    const { fieldId, occurrence, shift } = findFieldOccurrence(
      fields,
      { ...highlight, offset: adjustedOffset },
      isRescore,
    );
    cumulativeShift += shift;
    return { ...highlight, fieldId, occurrence };
  });
};

export const useHighlights = defineStore('highlights', () => {
  const bodyMessages = ref({});
  const inputFields = ref([]);
  const title = ref('');
  const location = ref('');
  const isRescore = ref(false);

  // add text, textArray, start, textLength to each field
  const fields = computed(() => {
    let currentFieldStart = 0;
    return inputFields.value.map((field) => {
      let html = '';
      (field.sections || []).forEach((section) => {
        (section.blocks || []).forEach((block) => {
          html += block.html;
        });
      });

      const text = htmlToText(html);
      const textArray = toArray(text);
      const textLength = textArray.length;
      const start = currentFieldStart;

      currentFieldStart += textLength;
      return {
        ...field, html, text, textArray, start, textLength,
      };
    });
  });

  // put description, title and location highlights into one array sorted by offset
  const allHighlights = computed(() => {
    const all = [];

    Object.keys(bodyMessages.value || {}).forEach((id) => {
      ['description', 'title', 'location_raw'].forEach((type) => {
        const message = bodyMessages.value[id];

        if (message.is_nudge) {
          return;
        }

        if (message.action === 'suppress') {
          return;
        }

        message.highlights[type].forEach(({ offset, length }, index) => {
          const text = message.variables.highlighted_text;
          all.push({
            offset,
            length,
            type,
            messageId: id,
            id: `${id}-${index}`,
            highlightedText: text,
            issue: message.issue,
            className: message.severity_class,
            action: message.action,
            tooltip_long: message.tooltip_long,
            tooltip_short: message.tooltip_short,
            ruleId: message.ruleId,
            childCategoryId: message.child_category_id,
            suggestions: message.suggestions,
            readMoreUrl: message.read_more_url,
          });
        });
      });
    });

    all.sort((a, b) => a.offset - b.offset);
    return all;
  });

  const descriptionHighlights = computed(() => adjustHighlights(
    fields.value,
    allHighlights.value.filter(({ type }) => type === 'description'),
    isRescore.value,
  ));

  const titleHighlights = computed(() => {
    const text = title.value || '';
    const field = {
      id: 'title', start: 0, text, textLength: text.length,
    };

    // apply 'insert_after_hl' and 'insert_before_hl' highlights last
    const highlights = allHighlights.value.filter(({ type }) => type === 'title');
    highlights.sort((a, b) => {
      const aWeight = ['insert_after_hl', 'insert_before_hl'].includes(a.action) ? 1 : 0;
      const bWeight = ['insert_after_hl', 'insert_before_hl'].includes(b.action) ? 1 : 0;
      return aWeight - bWeight;
    });

    return adjustHighlights(
      [field],
      highlights,
      isRescore.value,
    );
  });

  const locationHighlights = computed(() => {
    const text = location.value || '';
    const field = {
      id: 'location', start: 0, text, textLength: text.length,
    };
    return adjustHighlights(
      [field],
      allHighlights.value.filter(({ type }) => type === 'location_raw'),
      isRescore.value,
    );
  });

  const updateBodyMessages = ({
    bodyMessages: newBodyMessages = {},
    fields: newInputFields = [],
    title: newTitle = '',
    location: newLocation = '',
    isRescore: newIsRescore = false,
  }) => {
    bodyMessages.value = newBodyMessages;
    inputFields.value = newInputFields;
    title.value = newTitle;
    location.value = newLocation;
    isRescore.value = newIsRescore;
  };

  return {
    bodyMessages,
    inputFields,
    updateBodyMessages,
    descriptionHighlights,
    titleHighlights,
    locationHighlights,
  };
});
